import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Breadcrumb } from '../../components/Breadcrumb';
import { PageTitle } from '../../components/PageTitle';
import { ContactSection } from '../features/ContactSection';

/**
 * ServiceProductPage Component
 * Displays detailed information about the FADEN product brand offered by Lib-Gate
 */

const PAGETITLE_JP = 'プロダクト';
const PAGETITLE_EN = 'Product';

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const ServiceProductPageContainer = styled.div.attrs({className: 'page-container'})`
  background: #F9FAFA;
  min-height: 100vh;
  padding: 0 0 170px;

  @media (max-width: 768px) {
    padding: 0 20px 131px;
`;



const BrandOverviewSection = styled.section.attrs({className: 'brand-overview-section'})`
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  padding: 126px 0;

  @media (max-width: 768px) {
    padding: 60px 0 0;
    margin: 0;
  }
`;

const BrandOverviewTitle = styled.h2.attrs({className: 'brand-overview-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5em;
  letter-spacing: 0.05em;
  color: #333333;
  text-align: left;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
    margin: 0;
  }
`;

const BrandOverviewContent = styled.div.attrs({className: 'brand-overview-content'})`
  display: flex;
  flex-direction: column;
  gap: 89px;

  @media (max-width: 768px) {
    gap: 60px;
  }
`;

const BrandOverviewText = styled.div.attrs({className: 'brand-overview-text'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75em;
  color: #333333;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BrandOverviewImage = styled.picture.attrs({className: 'brand-overview-image'})`

  

  img {
    width: 100%;
    height: auto;
  }
  @media (max-width: 768px) {
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
    height: auto;
    border-radius: 0;
  }
`;


const BrandPhilosophySection = styled.section.attrs({className: 'brand-philosophy-section'})`
  padding-top: 151px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
  padding-top:60px;
    margin: 0;
  }
`;

const BrandPhilosophyContent = styled.div.attrs({className: 'brand-philosophy-content'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BrandPhilosophyImage = styled.div.attrs({className: 'brand-philosophy-image'})<{ $imageSrc: string }>`
  width: 580px;
  height: 336px;
  background-image: url(${props => props.$imageSrc});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 20px;


  @media (max-width: 768px) {
    display: none;
    aspect-ratio: 1/1;
    border-radius: 0;
    order: 1;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }
`;

const BrandPhilosophyImageSP = styled(BrandPhilosophyImage).attrs({className: 'brand-philosophy-image-sp'})`
    display: none;
    @media (max-width: 768px) {
      justify-self: center;
      display: unset;
      width: 375px;
      height: 375px;
      //margin: 0;
    }

`;

const BrandPhilosophyText = styled.div.attrs({className: 'brand-philosophy-text'})`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const BrandPhilosophyTitle = styled.h3.attrs({className: 'brand-philosophy-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const BrandPhilosophyDescription = styled.div.attrs({className: 'brand-philosophy-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  text-align: justify;

  p {
    margin: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;

    p {
      margin: 0 0 15px 0;
    }
  }
`;

const ProductsSection = styled.section.attrs({className: 'products-section'})`
  padding-top: 160px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
  padding-top:60px;
    margin: 0;
  }
`;

const ProductsContent = styled.div.attrs({className: 'products-content'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px 90px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const ProductsText = styled.div.attrs({className: 'products-text'})`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-end;

  @media (max-width: 768px) {
    gap: 30px;
    align-items: flex-start;
  }
`;

const ProductsTitle = styled.h3.attrs({className: 'products-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  align-self: flex-start;

  @media (max-width: 768px) {
    font-size: 20px;
    margin:0;
  }
`;

const ProductsDescription = styled.div.attrs({className: 'products-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  max-width: 610px;
  text-align: justify;

  p {
    margin: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
    max-width: none;
  }
`;

const ExternalLinksContainer = styled.div.attrs({className: 'external-links-container'})`
  display: flex;
  gap: 20px;
  margin-top: 30px;

  @media (max-width: 768px) {
  flex-direction: column;
    gap: 20px;
    margin-top: 0;
  }
`;

const ExternalLink = styled.a.attrs({className: 'external-link'})`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38;
  letter-spacing: 0.05em;
  color: #333333;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #0F3F8E;
  }

  @media (max-width: 768px) {
    gap: 20px;
    font-size: 13px;
    line-height: 1.38;
    letter-spacing: 0.05em;
  }
`;

const ExternalLinkIcon = styled.img.attrs({className: 'external-link-icon'})`
  width: 16px;
  height: 16px;
`;

const ProductsImage = styled.div.attrs({className: 'product-image'})<{ $imageSrc: string }>`
  width: 580px;
  height: 336px;
  background-image: url(${props => props.$imageSrc});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 20px;


  @media (max-width: 768px) {
    display: none;
    aspect-ratio: 1/1;
    border-radius: 0;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }
`;

const ProductsImageSP = styled(ProductsImage).attrs({className: 'product-image-sp'})`
    display: none;
    @media (max-width: 768px) {
      display: unset;
      width: 375px;
      height: 375px;
      justify-self: center;
      //margin: 0;
    }

`;
//   @media (max-width: 768px) {
//     padding: 120px 20px;
//   }
// `;

const PortfolioSection = styled.section.attrs({className: 'portfolio-section'})`
  margin-left: -20px;
  margin-right: -20px;
  padding-top: 140px;
  scroll-snap-align: start;

  @media (max-width: 768px) {
    padding-top: 60px;
    
    /* Mobile-specific spacing */
    & > * + * {
      margin-top: 30px;
    }
  }
`;

const PortfolioGrid = styled.div.attrs({className: 'portfolio-grid'})`
  //display: flex;
  //justify-content: center;
  //gap: 40px;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;

  // @media (max-width: 768px) {
  //   gap: 20px;
  // }
`;

const PortfolioImages = styled.div.attrs({className: 'portfolio-images'})`
  display:  inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  animation: ${scroll} 100s linear infinite;

  @media (max-width: 768px) {
    display: none;
    
    /* Mobile-specific logo layout */
    justify-content: space-around;
    
    & > * {
      flex: 0 0 auto;
    }
  }
`;

const PortfolioImagesSP = styled(PortfolioImages).attrs({className: 'portfolio-images-sp'})`
  @media (max-width: 768px) {
    display: inline-flex;
    }
 
  }
`;

const PortfolioImageWrapper = styled.div`
  padding-right:60px;
  @media (max-width: 768px) {
    padding-right: 40px;
  }
  `;

const PortfolioImage = styled.img.attrs({className: 'portfolio-image'})`
  width: 600px;
  height: 336px;
  border-radius: 10px;
  object-fit: contain;
  flex-shrink: 0;
  object-fit: contain;

  @media (max-width: 768px) {
 
  }
`;

const PortfolioImageSP = styled(PortfolioImage).attrs({className: 'portfolio-image-sp'})`
  @media (max-width: 768px) {
    width: 280px;
    height: 250px;
  }
`;

const OtherSolutionsSection = styled.section.attrs({className: 'other-solutions-section'})`
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const OtherSolutionsTitle = styled.h2.attrs({className: 'other-solutions-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0 0 80px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 40px 0;
  }
`;

const OtherSolutionsGrid = styled.div.attrs({className: 'other-solutions-grid'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const OtherSolutionCard = styled(Link).attrs({className: 'other-solution-card'})`
  text-decoration: none;
  background: #FFFFFF;
  border-radius: 20px;
  padding: 50px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    padding: 30px 20px;
    gap: 20px;
  }
`;

const OtherSolutionTitle = styled.h3.attrs({className: 'other-solution-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const OtherSolutionDescription = styled.p.attrs({className: 'other-solution-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }
`;

const OtherSolutionButton = styled.div.attrs({className: 'other-solution-button'})`
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38;
  letter-spacing: 0.05em;
  color: #333333;
  text-decoration: none;
  transition: color 0.3s ease;
  align-self: flex-end;

  &:hover {
    color: #0F3F8E;
  }
`;

const ServiceProductPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceProductPageContainer>
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: "事業紹介", path: "/service/" }, // 2階層目（リンクあり）
          { label: PAGETITLE_JP }           // 3階層目（現在のページなのでリンクなし）
        ]} 
      />

      {/* Page Title Section */}
      <PageTitle title={ PAGETITLE_JP } subtitle={ PAGETITLE_EN } />

      {/* Brand Overview Section */}
      <BrandOverviewSection>
        <BrandOverviewContent>
          <BrandOverviewTitle>FADEN®︎について</BrandOverviewTitle>
          <BrandOverviewImage>
          <source media="(min-width: 769px)" srcSet="/assets/images/service/product/faden_main.jpg" />
            <img 
                src="/assets/images/service/product/faden_main_sp.jpg" 
                alt="FADEN®︎について" 
                loading="lazy" 
              />
          </BrandOverviewImage>
          <BrandOverviewText>
            FADENは、クリエイティブ部門のプロデューサーが立ち上げた「機能にふつうのカタチを。」をコンセプトに、2020年より本格始動した多機能バッグブランドです。クラウドファンディングでの成功を足がかりに、機能性とデザイン性を高次元で融合させた製品開発に注力しています。
          </BrandOverviewText>
        </BrandOverviewContent>
      </BrandOverviewSection>

      {/* Brand Philosophy Section */}
      <BrandPhilosophySection>
        <BrandPhilosophyContent>
          <BrandPhilosophyImage $imageSrc="/assets/images/service/product/faden_580_336.jpg" />
          <BrandPhilosophyImageSP $imageSrc="/assets/images/service/product/faden_375_375.jpg" />
          <BrandPhilosophyText>
            <BrandPhilosophyTitle>ブランドの哲学</BrandPhilosophyTitle>
            <BrandPhilosophyDescription>
              <p>FADENは、多様な機能を持つバッグが市場に存在する中で、そのデザインが時に過度に個性的であったり、洗練さに欠けたりする現状に一石を投じます。</p>
              <p>これを「アンチ・ユニークイズム」という精神として掲げています。</p>
              <p>バッグは、その日の必需品を運び、日常を支えるという基本的な役割が大前提です。</p>
              <p>しかし、それだけでなく、ファッションの一部として全体の調和を崩さず、むしろ引き立てる存在であるべきだと考えます。</p>
              <p>この哲学に基づき、FADENのバッグは邪魔をしない普遍的なフォルムと、徹底的に使いやすさを追求した機能性を両立させています。</p>
            </BrandPhilosophyDescription>
          </BrandPhilosophyText>
        </BrandPhilosophyContent>
      </BrandPhilosophySection>

      {/* Products Section */}
      <ProductsSection>
        <ProductsContent>
          <ProductsText>
            <ProductsTitle>製品と展開</ProductsTitle>
            <ProductsDescription>
              <p>20個の機能を装備した代表作バックパック020シリーズをはじめ、FADENは多機能バッグの新たなスタンダードを提案してきました。</p>
              <p>近年では、レザーアイテムにもその領域を広げ、着実に事業の視野を広げています。</p>
              <p>単に多くの機能を詰め込むだけでなく、日々のあらゆるシーンに自然に溶け込み、ファッションアイテムとしての調和を損なわないデザインを重視しています。</p>
              <p>「アンチ・ユニークイズム」の精神に基づき、機能性とデザインの新たな調和を追求し、お客様のライフスタイルをより豊かにする製品を提供し続けてまいります。</p>
            </ProductsDescription>
          </ProductsText>
          <ProductsImage $imageSrc="/assets/images/service/product/faden_580_336_002.jpg" />
          <ProductsImageSP $imageSrc="/assets/images/service/product/faden_375_375_002.jpg" />
          <ExternalLinksContainer>
              <ExternalLink href="https://faden.tokyo/" target="_blank" rel="noopener noreferrer">
                ブランドサイト
                <ExternalLinkIcon src="/images/link-right-icon-inverted.svg" alt="link right"/>
              </ExternalLink>
              <ExternalLink href="https://www.instagram.com/faden_tokyo/" target="_blank" rel="noopener noreferrer">
                FADENブランドインスタグラム
                <ExternalLinkIcon src="/images/link-right-icon-inverted.svg" alt="link right"/>
              </ExternalLink>
            </ExternalLinksContainer>
        </ProductsContent>
      </ProductsSection>

      {/* Portfolio Section */}
      <PortfolioSection>
        <PortfolioGrid>
            <PortfolioImages>
            {[
              '/assets/images/service/product/faden_600_336_001.jpg',
              '/assets/images/service/product/faden_600_336_002.jpg',
              '/assets/images/service/product/faden_600_336_003.jpg',
              '/assets/images/service/product/faden_600_336_004.jpg',
              '/assets/images/service/product/faden_600_336_005.jpg',
              '/assets/images/service/product/faden_600_336_006.jpg',
              '/assets/images/service/product/faden_600_336_007.jpg',
              '/assets/images/service/product/faden_600_336_008.jpg',
              // Repeat
              '/assets/images/service/product/faden_600_336_001.jpg',
              '/assets/images/service/product/faden_600_336_002.jpg',
              '/assets/images/service/product/faden_600_336_003.jpg',
              '/assets/images/service/product/faden_600_336_004.jpg',
              '/assets/images/service/product/faden_600_336_005.jpg',
              '/assets/images/service/product/faden_600_336_006.jpg',
              '/assets/images/service/product/faden_600_336_007.jpg',
              '/assets/images/service/product/faden_600_336_008.jpg',
            ].map((logo, index) => (
              <PortfolioImageWrapper><PortfolioImage key={index} src={logo} alt={`Portfolio Image ${index + 1}`} /></PortfolioImageWrapper>
            ))}
            </PortfolioImages>
            <PortfolioImagesSP>
            {[
              '/assets/images/service/product/faden_280_250_001.jpg',
              '/assets/images/service/product/faden_280_250_002.jpg',
              '/assets/images/service/product/faden_280_250_003.jpg',
              '/assets/images/service/product/faden_280_250_004.jpg',
              '/assets/images/service/product/faden_280_250_005.jpg',
              '/assets/images/service/product/faden_280_250_006.jpg',
              '/assets/images/service/product/faden_280_250_007.jpg',
              '/assets/images/service/product/faden_280_250_008.jpg',
              // Repeat
              '/assets/images/service/product/faden_280_250_001.jpg',
              '/assets/images/service/product/faden_280_250_002.jpg',
              '/assets/images/service/product/faden_280_250_003.jpg',
              '/assets/images/service/product/faden_280_250_004.jpg',
              '/assets/images/service/product/faden_280_250_005.jpg',
              '/assets/images/service/product/faden_280_250_006.jpg',
              '/assets/images/service/product/faden_280_250_007.jpg',
              '/assets/images/service/product/faden_280_250_008.jpg',
            ].map((logo, index) => (
              <PortfolioImageWrapper><PortfolioImageSP key={index} src={logo} alt={`Portfolio Image ${index + 1}`} /></PortfolioImageWrapper>
            ))}
          </PortfolioImagesSP>
        </PortfolioGrid>
      </PortfolioSection>

      {/* Contact Section */}
      <ContactSection />

      {/* Other Solutions Section */}
      <OtherSolutionsSection>
        <OtherSolutionsTitle>他のソリューションを見る</OtherSolutionsTitle>
        <OtherSolutionsGrid>
          <OtherSolutionCard to="/service/development/">
            <OtherSolutionTitle>開発</OtherSolutionTitle>
            <OtherSolutionDescription>
              クライアントのビジネスを深く理解し、最適なソリューションを提供することで、課題解決と事業成長に貢献します。
            </OtherSolutionDescription>
            <OtherSolutionButton>
              詳しく見る
              <img src="/images/arrow-right.svg" alt="arrow right" />
            </OtherSolutionButton>
          </OtherSolutionCard>
          <OtherSolutionCard to="/service/creative/">
            <OtherSolutionTitle>クリエイティブ</OtherSolutionTitle>
            <OtherSolutionDescription>
              Webサイトやランディングページ制作からブランディング戦略立案まで、お客様のデジタルプレゼンスを最大化します。
            </OtherSolutionDescription>
            <OtherSolutionButton>
              詳しく見る
              <img src="/images/arrow-right.svg" alt="arrow right" />
            </OtherSolutionButton>
          </OtherSolutionCard>
        </OtherSolutionsGrid>
      </OtherSolutionsSection>

    </ServiceProductPageContainer>
  );
};

export default ServiceProductPage;
